import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Brain, TrendingUp, Loader2, RefreshCw, Lightbulb,
  ThumbsDown, CheckCircle, BarChart3, Zap, ChevronDown, ChevronUp
} from 'lucide-react';


const apiHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('hw_token')}`,
  'Content-Type': 'application/json'
});

export default function PerformanceInsightsPanel({ pages }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/social/analytics/insights`, { headers: apiHeaders() });
      if (r.ok) setInsights(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/api/social/analytics/analyze`, {
        method: 'POST', headers: apiHeaders()
      });
      if (r.ok) {
        const data = await r.json();
        alert(`Analisi completata!\n${data.analyzed} pagine analizzate, ${data.skipped} saltate (dati insufficienti)`);
        fetchInsights();
      }
    } catch (e) { console.error(e); }
    setAnalyzing(false);
  };

  return (
    <div className="space-y-4" data-testid="performance-insights-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-violet-600" />
          <h3 className="font-semibold text-slate-800">AI Performance Learning</h3>
          <Badge className="bg-violet-100 text-violet-700 text-[10px]">Automatico</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchInsights} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={runAnalysis} disabled={analyzing}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="run-analysis-btn"
          >
            {analyzing ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analisi in corso...</>
            ) : (
              <><Zap className="h-3.5 w-3.5 mr-1.5" /> Analizza Performance</>
            )}
          </Button>
        </div>
      </div>

      {/* Description */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm text-violet-800">
        <p>L'AI analizza automaticamente le performance dei post pubblicati <strong>ogni lunedì alle 9:00</strong>.
        Identifica cosa funziona meglio per ogni pagina e adatta automaticamente i contenuti futuri.
        Puoi anche lanciare l'analisi manualmente.</p>
      </div>

      {/* Insights list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
        </div>
      ) : insights.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Brain className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nessuna analisi disponibile</p>
          <p className="text-xs mt-1">Pubblica almeno 3 post per pagina, poi clicca "Analizza Performance"</p>
        </div>
      ) : (
        <div className="space-y-2">
          {insights.map(insight => (
            <Card key={insight.page_id} className="border shadow-sm">
              <CardContent className="p-0">
                {/* Page header - clickable */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedPage(expandedPage === insight.page_id ? null : insight.page_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {insight.page_name?.replace('Holiday World ', '').charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{insight.page_name}</div>
                      <div className="text-[10px] text-slate-400">
                        {insight.posts_analyzed} post analizzati - {insight.analyzed_at?.slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-bold text-violet-700">{insight.avg_engagement_rate}%</div>
                      <div className="text-[9px] text-slate-400">Engagement medio</div>
                    </div>
                    {expandedPage === insight.page_id ?
                      <ChevronUp className="h-4 w-4 text-slate-400" /> :
                      <ChevronDown className="h-4 w-4 text-slate-400" />
                    }
                  </div>
                </div>

                {/* Expanded details */}
                {expandedPage === insight.page_id && (
                  <div className="border-t px-4 py-3 space-y-3 bg-slate-50/50">
                    {/* Patterns */}
                    {insight.patterns && (
                      <div>
                        <div className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                          <BarChart3 className="h-3 w-3" /> Pattern Identificati
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="bg-white rounded p-2 border">
                            <div className="text-[10px] text-slate-400">Pilastro migliore</div>
                            <div className="text-xs font-medium text-slate-700">{insight.patterns.best_pillar || '-'}</div>
                          </div>
                          <div className="bg-white rounded p-2 border">
                            <div className="text-[10px] text-slate-400">Verticale migliore</div>
                            <div className="text-xs font-medium text-slate-700">{insight.patterns.best_vertical || '-'}</div>
                          </div>
                          <div className="bg-white rounded p-2 border">
                            <div className="text-[10px] text-slate-400">Tono efficace</div>
                            <div className="text-xs font-medium text-slate-700">{insight.patterns.best_tone || '-'}</div>
                          </div>
                          <div className="bg-white rounded p-2 border">
                            <div className="text-[10px] text-slate-400">Lunghezza ideale</div>
                            <div className="text-xs font-medium text-slate-700">{insight.patterns.best_content_length || '-'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Content Rules - what AI learned */}
                    {insight.content_rules?.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-emerald-600 mb-1.5 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Regole Apprese dall'AI
                        </div>
                        <div className="space-y-1">
                          {insight.content_rules.map((rule, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-700 bg-emerald-50 rounded px-2 py-1">
                              <Zap className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                              {rule}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {insight.recommendations?.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-amber-600 mb-1.5 flex items-center gap-1">
                          <Lightbulb className="h-3 w-3" /> Raccomandazioni
                        </div>
                        <div className="space-y-1">
                          {insight.recommendations.map((rec, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-700 bg-amber-50 rounded px-2 py-1">
                              <TrendingUp className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                              {rec}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* What to avoid */}
                    {insight.avoid?.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-rose-600 mb-1.5 flex items-center gap-1">
                          <ThumbsDown className="h-3 w-3" /> Da Evitare
                        </div>
                        <div className="space-y-1">
                          {insight.avoid.map((item, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-700 bg-rose-50 rounded px-2 py-1">
                              <ThumbsDown className="h-3 w-3 text-rose-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Weekly Summary */}
                    {insight.weekly_summary && (
                      <div className="bg-white rounded p-2 border text-[11px] text-slate-600 italic">
                        "{insight.weekly_summary}"
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
